import { ChangeEvent, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import {
    ContactRequestHistoryEntry,
    ContactRequestType,
    ContactRequestUpdateFields,
    ContactRequestsPagination,
    REQUESTS_UNREAD_EVENT,
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_OPTIONS,
} from "../../../../DataTypes/contactRequests";
import { flipTooltipIfNeeded, getRoundedLocalDateTimeString } from "../../../../Helpers/Helpers";
import '../outreach-styles.css'
import './requests-styles.css'

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

type SortOption = 'recent' | 'oldest'
type TriState = '' | 'true' | 'false'

const SORT_LABELS: Record<SortOption, string> = {
    recent: 'Newest first',
    oldest: 'Oldest first',
}

const TRI_STATE_VALUES: TriState[] = ['', 'true', 'false']

const parseTriState = (value: string | null): TriState =>
    (TRI_STATE_VALUES as string[]).includes(value || '') ? value as TriState : ''

const triStateToBool = (value: TriState) => value ? value === 'true' : undefined

const parseFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    const page = Number(params.get('page'))
    const sort = params.get('sort') || 'recent'
    return {
        page: Number.isInteger(page) && page > 0 ? page : 1,
        search: params.get('search') || '',
        status: params.get('status') || 'all',
        isRead: parseTriState(params.get('is_read')),
        isReplied: parseTriState(params.get('is_replied')),
        types: params.get('types') || '',
        dateFrom: params.get('date_from') || '',
        dateTo: params.get('date_to') || '',
        sort: (sort in SORT_LABELS ? sort : 'recent') as SortOption,
    }
}

type LeadManagerType = {
    id: number;
    email: string;
    username: string;
}

type RequestsPageState = {
    requests: ContactRequestType[];
    pagination: ContactRequestsPagination | null;
    unreadCount: number;
    leadManagers: LeadManagerType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    updatingId: number | null;
    page: number;
    search: string;
    status: string;
    isRead: TriState;
    isReplied: TriState;
    types: string;
    dateFrom: string;
    dateTo: string;
    sort: SortOption;
    // Detail panel
    detailId: number | null;
    detail: ContactRequestType | null;
    history: ContactRequestHistoryEntry[];
    detailLoading: boolean;
    detailError: string | null;
    notesDraft: string;
    statusNote: string;
    leadIdDraft: string;
}

class RequestsPage extends ViewItem {
    state: RequestsPageState;
    searchTimer: any = null;

    constructor(props: any) {
        super(props)
        this.state = {
            requests: [],
            pagination: null,
            unreadCount: 0,
            leadManagers: [],
            loading: true,
            error: null,
            needsAuth: false,
            updatingId: null,
            detailId: null,
            detail: null,
            history: [],
            detailLoading: false,
            detailError: null,
            notesDraft: '',
            statusNote: '',
            leadIdDraft: '',
            ...parseFiltersFromUrl(),
        }
    }

    componentDidMount(): void {
        this.syncUrlWithState()
        this.loadRequests()
        this.loadLeadManagers()
    }

    componentWillUnmount(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer)
    }

    syncUrlWithState = () => {
        const { page, search, status, isRead, isReplied, types, dateFrom, dateTo, sort } = this.state
        const params = new URLSearchParams()
        if (page > 1) params.set('page', String(page))
        if (search) params.set('search', search)
        if (status !== 'all') params.set('status', status)
        if (isRead) params.set('is_read', isRead)
        if (isReplied) params.set('is_replied', isReplied)
        if (types) params.set('types', types)
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (sort !== 'recent') params.set('sort', sort)
        const qs = params.toString()
        window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''))
    }

    applyFilters = () => {
        this.syncUrlWithState()
        this.loadRequests()
    }

    setUnreadCount = (count: number) => {
        this.setState({ unreadCount: count })
        window.dispatchEvent(new CustomEvent(REQUESTS_UNREAD_EVENT, { detail: count }))
    }

    loadLeadManagers = async () => {
        const response = await controllers().outreach.getLeadManagers()
        if (response && response.statusCode === 200) {
            this.setState({ leadManagers: response.data?.managers || [] })
        }
    }

    loadRequests = async () => {
        const { page, search, status, isRead, isReplied, types, dateFrom, dateTo, sort } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().outreach.getContactRequests({
                page,
                per_page: PER_PAGE,
                search: search || undefined,
                status: status !== 'all' ? status : undefined,
                is_read: triStateToBool(isRead),
                is_replied: triStateToBool(isReplied),
                types: types || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                sort,
            })
            if (!response || response.statusCode !== 200) {
                this.setState({
                    loading: false,
                    error: response?.error || 'Failed to load requests.',
                })
                return
            }
            this.setState({
                requests: response.data?.requests || [],
                pagination: response.data?.pagination || null,
                loading: false,
            })
            this.setUnreadCount(response.data?.unread_count || 0)
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load requests.' })
        }
    }

    // Free-text inputs share one debounce so typing doesn't fire a request per keystroke.
    handleTextFilterChange = (field: 'search' | 'types') => (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ [field]: e.target.value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.applyFilters)
        }, SEARCH_DEBOUNCE_MS)
    }

    handleFilterChange = (field: 'status' | 'isRead' | 'isReplied' | 'dateFrom' | 'dateTo' | 'sort') =>
        (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
            this.setState({ [field]: e.target.value, page: 1 }, this.applyFilters)
        }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.applyFilters)
    }

    // Keeps the table row and the open detail panel in step with whatever the
    // backend returned, and adjusts the unread badge if is_read flipped.
    applyUpdatedRequest = (updated: ContactRequestType) => {
        const previous = this.state.requests.find(r => r.id === updated.id)
            || (this.state.detail?.id === updated.id ? this.state.detail : null)
        if (previous && previous.is_read !== updated.is_read) {
            this.setUnreadCount(Math.max(0, this.state.unreadCount + (updated.is_read ? -1 : 1)))
        }
        this.setState((prev: RequestsPageState) => ({
            requests: prev.requests.map(r => r.id === updated.id ? updated : r),
            detail: prev.detail?.id === updated.id ? updated : prev.detail,
        }))
    }

    updateRequest = async (req: ContactRequestType, fields: ContactRequestUpdateFields) => {
        this.setState({ updatingId: req.id })
        const response = await controllers().outreach.updateContactRequest(req.id, fields)
        if (response && response.statusCode === 200 && response.data) {
            this.applyUpdatedRequest(response.data)
            if (this.state.detailId === req.id) this.reloadHistory(req.id)
        } else {
            controllers().alert.show('error', response?.error || 'Failed to update request.')
        }
        this.setState({ updatingId: null })
        return response
    }

    openDetail = async (id: number) => {
        this.setState({ detailId: id, detail: null, history: [], detailLoading: true, detailError: null, statusNote: '' })
        const response = await controllers().outreach.getContactRequestDetail(id)
        if (this.state.detailId !== id) return
        if (!response || response.statusCode !== 200) {
            this.setState({ detailLoading: false, detailError: response?.error || 'Failed to load request.' })
            return
        }
        const detail: ContactRequestType = response.data?.request
        this.setState({
            detailLoading: false,
            history: response.data?.history || [],
            notesDraft: detail?.internal_notes || '',
            leadIdDraft: detail?.lead_id ? String(detail.lead_id) : '',
        })
        // The detail endpoint marks the request read, so this also clears its unread state.
        if (detail) this.applyUpdatedRequest(detail)
        this.setState({ detail })
    }

    reloadHistory = async (id: number) => {
        const response = await controllers().outreach.getContactRequestDetail(id)
        if (this.state.detailId !== id || !response || response.statusCode !== 200) return
        this.setState({ history: response.data?.history || [] })
    }

    closeDetail = () => {
        this.setState({ detailId: null, detail: null, history: [], detailError: null })
    }

    handleDescriptionHover = (e: MouseEvent<HTMLDivElement>) => {
        flipTooltipIfNeeded(e.currentTarget)
    }

    renderStatusSelect = (req: ContactRequestType, extra: ContactRequestUpdateFields = {}) => {
        // Keep an unknown backend status selectable rather than silently showing the first option.
        const options = REQUEST_STATUS_LABELS[req.status]
            ? REQUEST_STATUS_OPTIONS
            : [...REQUEST_STATUS_OPTIONS, [req.status, req.status_display || req.status] as [string, string]]
        return (
            <select
                className={`lead-status-select requests-status-${req.status}`}
                value={req.status}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => this.updateRequest(req, { status: e.target.value, ...extra })}
                disabled={this.state.updatingId === req.id}
            >
                {options.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                ))}
            </select>
        )
    }

    renderRepliedToggle = (req: ContactRequestType) => (
        <button
            className={`outreach-toggle ${req.is_replied ? 'outreach-toggle-on' : ''}`}
            onClick={(e) => { e.stopPropagation(); this.updateRequest(req, { replied: !req.is_replied }) }}
            disabled={this.state.updatingId === req.id}
            aria-label={req.is_replied ? 'Mark as not replied' : 'Mark as replied'}
            title={req.is_replied
                ? `Replied${req.replied_by ? ` by ${req.replied_by}` : ''}${req.replied_at ? ` · ${getRoundedLocalDateTimeString(req.replied_at)}` : ''} — click to undo`
                : 'Not replied yet'}
        >
            <span className="outreach-toggle-knob" />
        </button>
    )

    renderContact = (contact: string) => {
        if (!contact) return <span className="outreach-muted">—</span>
        if (contact.includes('@')) {
            return <a className="requests-link" href={`mailto:${contact}`} onClick={(e) => e.stopPropagation()}>{contact}</a>
        }
        return <span>{contact}</span>
    }

    renderTypes = (types: string) => {
        if (!types) return <span className="outreach-muted">—</span>
        return (
            <div className="requests-tags">
                {types.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="requests-tag">{t}</span>
                ))}
            </div>
        )
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_count} total
                </span>
                <div className="outreach-pagination-controls">
                    <button
                        className="outreach-pagination-btn"
                        disabled={!pagination.has_previous}
                        onClick={() => this.goToPage(page - 1)}
                    >
                        Previous
                    </button>
                    <button
                        className="outreach-pagination-btn"
                        disabled={!pagination.has_next}
                        onClick={() => this.goToPage(page + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderTable = () => {
        const { requests, detailId } = this.state
        return (
            <div className="outreach-table-wrap">
                <table className="outreach-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Description</th>
                            <th>Types</th>
                            <th>Status</th>
                            <th>Assigned</th>
                            <th>Received</th>
                            <th>Replied</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map(req => (
                            <tr
                                key={req.id}
                                className={`requests-row${req.is_read ? '' : ' requests-row-unread'}${detailId === req.id ? ' lead-is-focused' : ''}`}
                                onClick={() => this.openDetail(req.id)}
                            >
                                <td>
                                    <div className="outreach-company-cell">
                                        <div className="outreach-company-name-row">
                                            {!req.is_read && <span className="requests-unread-dot" title="Unread" />}
                                            <span className="outreach-company-name">{req.name || '—'}</span>
                                            {req.attachment && <span className="requests-attachment-icon" title={req.attachment_name || 'Attachment'}>📎</span>}
                                        </div>
                                        {req.business_name && (
                                            <div className="outreach-dm-inline">{req.business_name}</div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="outreach-company-cell">
                                        {this.renderContact(req.contact)}
                                        {req.phone && <div className="outreach-dm-inline">{req.phone}</div>}
                                    </div>
                                </td>
                                <td>
                                    {req.description ? (
                                        <div className="outreach-notes-cell" onMouseEnter={this.handleDescriptionHover}>
                                            <span className="outreach-notes-truncated">{req.description}</span>
                                            <div className="outreach-notes-tooltip">{req.description}</div>
                                        </div>
                                    ) : (
                                        <span className="outreach-muted">—</span>
                                    )}
                                </td>
                                <td>{this.renderTypes(req.types)}</td>
                                <td>{this.renderStatusSelect(req)}</td>
                                <td className={req.assigned_to ? '' : 'outreach-muted'}>{req.assigned_to || '—'}</td>
                                <td className="outreach-muted">{req.created_at ? getRoundedLocalDateTimeString(req.created_at) : '—'}</td>
                                <td>{this.renderRepliedToggle(req)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    renderDetailField = (label: string, value: ReactNode) => (
        <div className="requests-detail-field">
            <div className="requests-detail-label">{label}</div>
            <div className="requests-detail-value">{value || <span className="outreach-muted">—</span>}</div>
        </div>
    )

    renderHistoryChanges = (changes: ContactRequestHistoryEntry['changes']) => {
        if (!changes || !Object.keys(changes).length) return null
        const fmt = (v: any) => v === null || v === undefined || v === '' ? '—' : String(v)
        return (
            <ul className="requests-history-changes">
                {Object.entries(changes).map(([field, change]) => (
                    <li key={field}>
                        <span className="requests-history-field">{field.replace(/_/g, ' ')}</span>
                        {' '}{fmt(change?.old)} → {fmt(change?.new)}
                    </li>
                ))}
            </ul>
        )
    }

    renderDetail = () => {
        const { detailId, detail, history, detailLoading, detailError, notesDraft, statusNote, leadIdDraft, leadManagers, updatingId } = this.state
        if (detailId === null) return null
        const busy = updatingId === detailId

        return (
            <div className="outreach-modal-overlay requests-detail-overlay" onClick={this.closeDetail}>
                <aside className="requests-detail" onClick={(e) => e.stopPropagation()}>
                    <div className="requests-detail-header">
                        <div>
                            <h3 className="outreach-modal-title">{detail?.name || `Request #${detailId}`}</h3>
                            {detail?.business_name && <p className="outreach-modal-subtitle">{detail.business_name}</p>}
                        </div>
                        <button className="requests-detail-close" onClick={this.closeDetail} aria-label="Close">×</button>
                    </div>

                    {detailLoading && (
                        <div className="outreach-state"><div className="outreach-spinner" /></div>
                    )}

                    {detailError && (
                        <div className="outreach-banner outreach-banner-error">
                            <span className="outreach-banner-text">{detailError}</span>
                            <button className="outreach-banner-action" onClick={() => this.openDetail(detailId)}>Retry</button>
                        </div>
                    )}

                    {detail && (
                        <>
                            <section className="requests-detail-section">
                                <div className="requests-detail-actions">
                                    <div className="requests-detail-field">
                                        <div className="requests-detail-label">Status</div>
                                        {this.renderStatusSelect(detail, statusNote ? { note: statusNote } : {})}
                                    </div>
                                    <div className="requests-detail-field">
                                        <div className="requests-detail-label">Replied</div>
                                        {this.renderRepliedToggle(detail)}
                                    </div>
                                    <div className="requests-detail-field">
                                        <div className="requests-detail-label">&nbsp;</div>
                                        <button
                                            className="outreach-pagination-btn"
                                            disabled={busy}
                                            onClick={() => this.updateRequest(detail, { is_read: false })}
                                        >
                                            Mark unread
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    className="outreach-search requests-detail-input"
                                    placeholder="Optional note for the next status change"
                                    value={statusNote}
                                    onChange={(e) => this.setState({ statusNote: e.target.value })}
                                />
                                {detail.is_replied && (
                                    <div className="outreach-dm-inline">
                                        Replied{detail.replied_by ? ` by ${detail.replied_by}` : ''}
                                        {detail.replied_at ? ` · ${getRoundedLocalDateTimeString(detail.replied_at)}` : ''}
                                    </div>
                                )}
                            </section>

                            <section className="requests-detail-section requests-detail-grid">
                                {this.renderDetailField('Contact', this.renderContact(detail.contact))}
                                {this.renderDetailField('Phone', detail.phone && <a className="requests-link" href={`tel:${detail.phone}`}>{detail.phone}</a>)}
                                {this.renderDetailField('Types', detail.types && this.renderTypes(detail.types))}
                                {this.renderDetailField('Coupon', detail.coupon && <span className="requests-coupon">{detail.coupon}</span>)}
                                {this.renderDetailField('Received', detail.created_at && getRoundedLocalDateTimeString(detail.created_at))}
                                {this.renderDetailField('Attachment', detail.attachment && (
                                    <a className="requests-link" href={detail.attachment} target="_blank" rel="noopener noreferrer">
                                        {detail.attachment_name || 'Open'} ↗
                                    </a>
                                ))}
                            </section>

                            <section className="requests-detail-section">
                                <div className="requests-detail-label">Description</div>
                                <div className="requests-detail-description">
                                    {detail.description || <span className="outreach-muted">—</span>}
                                </div>
                            </section>

                            <section className="requests-detail-section requests-detail-grid">
                                <div className="requests-detail-field">
                                    <div className="requests-detail-label">Assigned to</div>
                                    <select
                                        className="outreach-inline-select"
                                        value={detail.assigned_to_id || ''}
                                        disabled={busy}
                                        onChange={(e) => this.updateRequest(detail, { assigned_to_id: e.target.value ? Number(e.target.value) : null })}
                                    >
                                        <option value="">Unassigned</option>
                                        {leadManagers.map(m => (
                                            <option key={m.id} value={m.id}>{m.username || m.email}</option>
                                        ))}
                                        {detail.assigned_to_id && !leadManagers.some(m => m.id === detail.assigned_to_id) && (
                                            <option value={detail.assigned_to_id}>{detail.assigned_to || `User #${detail.assigned_to_id}`}</option>
                                        )}
                                    </select>
                                </div>
                                <div className="requests-detail-field">
                                    <div className="requests-detail-label">Linked lead</div>
                                    <div className="requests-lead-row">
                                        <input
                                            type="number"
                                            min={1}
                                            className="outreach-search requests-small-input"
                                            placeholder="Lead ID"
                                            value={leadIdDraft}
                                            onChange={(e) => this.setState({ leadIdDraft: e.target.value })}
                                        />
                                        {(leadIdDraft || '') !== (detail.lead_id ? String(detail.lead_id) : '') && (
                                            <button
                                                className="outreach-pagination-btn"
                                                disabled={busy}
                                                onClick={() => this.updateRequest(detail, { lead_id: leadIdDraft ? Number(leadIdDraft) : null })}
                                            >
                                                Save
                                            </button>
                                        )}
                                        {detail.lead_id && (
                                            <Link className="requests-link" to={`/outreach/${detail.lead_id}/edit`}>Open ↗</Link>
                                        )}
                                    </div>
                                </div>
                            </section>

                            <section className="requests-detail-section">
                                <div className="requests-detail-label">Internal notes</div>
                                <textarea
                                    className="requests-notes"
                                    rows={4}
                                    value={notesDraft}
                                    onChange={(e) => this.setState({ notesDraft: e.target.value })}
                                    placeholder="Visible to admins only"
                                />
                                {notesDraft !== (detail.internal_notes || '') && (
                                    <div className="requests-notes-actions">
                                        <button
                                            className="outreach-pagination-btn"
                                            onClick={() => this.setState({ notesDraft: detail.internal_notes || '' })}
                                        >
                                            Discard
                                        </button>
                                        <button
                                            className="outreach-add-btn"
                                            disabled={busy}
                                            onClick={() => this.updateRequest(detail, { internal_notes: notesDraft })}
                                        >
                                            Save notes
                                        </button>
                                    </div>
                                )}
                            </section>

                            {(detail.ip_address || detail.referer) && (
                                <section className="requests-detail-section requests-detail-grid">
                                    {this.renderDetailField('IP address', detail.ip_address)}
                                    {this.renderDetailField('Referer', detail.referer && (
                                        <span className="requests-break">{detail.referer}</span>
                                    ))}
                                </section>
                            )}

                            <section className="requests-detail-section">
                                <div className="requests-detail-label">History</div>
                                {history.length === 0 ? (
                                    <div className="outreach-muted">No activity yet.</div>
                                ) : (
                                    <ol className="requests-history">
                                        {history.map((h, i) => (
                                            <li key={i} className="requests-history-item">
                                                <div className="requests-history-head">
                                                    <span>{h.action_display || h.action}</span>
                                                    <span className="outreach-muted">{getRoundedLocalDateTimeString(h.created_at)}</span>
                                                </div>
                                                <div className="outreach-dm-inline">{h.user}</div>
                                                {this.renderHistoryChanges(h.changes)}
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </section>
                        </>
                    )}
                </aside>
            </div>
        )
    }

    render(): ReactNode {
        const { pagination, unreadCount, loading, error, needsAuth, requests, search, status, isRead, isReplied, types, dateFrom, dateTo, sort } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Requests</h1>
                        <p className="outreach-subtitle">
                            {pagination ? `${pagination.total_count} request${pagination.total_count === 1 ? '' : 's'}` : 'Contact requests'}
                            {unreadCount > 0 && ` · ${unreadCount} unread`}
                        </p>
                    </div>

                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search name, contact, business, phone..."
                            value={search}
                            onChange={this.handleTextFilterChange('search')}
                        />
                        <select className="outreach-status-select" value={status} onChange={this.handleFilterChange('status')}>
                            <option value="all">All statuses</option>
                            {REQUEST_STATUS_OPTIONS.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <select className="outreach-status-select" value={isRead} onChange={this.handleFilterChange('isRead')}>
                            <option value="">Read & unread</option>
                            <option value="false">Unread</option>
                            <option value="true">Read</option>
                        </select>
                        <select className="outreach-status-select" value={isReplied} onChange={this.handleFilterChange('isReplied')}>
                            <option value="">Any reply</option>
                            <option value="false">Not replied</option>
                            <option value="true">Replied</option>
                        </select>
                        <input
                            type="text"
                            className="outreach-search requests-small-input"
                            placeholder="Type"
                            value={types}
                            onChange={this.handleTextFilterChange('types')}
                        />
                        <input
                            type="date"
                            className="outreach-search requests-date-input"
                            value={dateFrom}
                            max={dateTo || undefined}
                            onChange={this.handleFilterChange('dateFrom')}
                            title="Received from"
                        />
                        <input
                            type="date"
                            className="outreach-search requests-date-input"
                            value={dateTo}
                            min={dateFrom || undefined}
                            onChange={this.handleFilterChange('dateTo')}
                            title="Received to"
                        />
                        <select className="outreach-status-select" value={sort} onChange={this.handleFilterChange('sort')}>
                            {Object.entries(SORT_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view requests.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadRequests}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && requests.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No requests found.
                    </div>
                )}

                {!loading && !error && !needsAuth && requests.length > 0 && (
                    <>
                        {this.renderTable()}
                        {this.renderPagination()}
                    </>
                )}

                {this.renderDetail()}
            </div>
        )
    }
}

export { RequestsPage }
